import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, OverlayTrigger, Tooltip, Alert, Modal, Dropdown, Pagination, ButtonGroup, DropdownButton } from 'react-bootstrap';
import { BiEdit, BiTrash, BiSearch, BiSortUp, BiSortDown, BiFirstPage, BiLastPage, BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { FaPlus, FaTruck } from 'react-icons/fa';
import Autosuggest from 'react-autosuggest';
import './RulesLayout.scss';
import './CarriageRules.scss'; // Import the new SCSS file

const CarriageRules = () => {
  const [rules, setRules] = useState([]);
  const [search, setSearch] = useState({
    carriageType: '2', // Default to 'Any'
    origin: '',
    destination: ''
  });
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [sortedRules, setSortedRules] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 100;

  const location = useLocation();
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (!showDeleteModal) {
      fetchRules();
    }
  }, [search, currentPage]);

  useEffect(() => {
    let sortedArray = [...rules];
    if (sortConfig.key !== null) {
      sortedArray.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    setSortedRules(sortedArray);
  }, [rules, sortConfig]);

  const fetchRules = async () => {
    try {
      const response = await axios.get('/api/carriage-rules', {
        params: {
          ...search,
          page: currentPage,
          limit: itemsPerPage
        }
      });
      setRules(response.data.rules);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
    } catch (error) {
      console.error('Error fetching carriage rules', error);
    }
  };

  const handleSearchChange = (e, { newValue } = {}) => {
    const { name, value } = e.target;
    setSearch((prevSearch) => ({
      ...prevSearch,
      [name]: newValue !== undefined ? newValue : value
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchRules();
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? <BiSortUp /> : <BiSortDown />;
  };

  const formatAppliesTo = (appliesTo) => {
    const maxLength = 120;
    if (appliesTo.length > maxLength) {
      return (
        <>
          {appliesTo.substring(0, maxLength)}
          <a href="#" className="more-link" onClick={(e) => handleMoreClick(e, appliesTo)}>
            ... more
          </a>
        </>
      );
    }
    return appliesTo;
  };

  const handleMoreClick = (e, fullText) => {
    e.preventDefault();
    const target = e.currentTarget;
    target.parentNode.innerHTML = fullText;
  };

  const handleDelete = (rule) => {
    setSelectedRule(rule);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/carriage-rule/${selectedRule.carriage_rule_id}`);
      setSuccessMessage('Carriage rule deleted successfully.');
      fetchRules();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting carriage rule', error);
    }
  };

  const fetchPortSuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/ports', { params: { query: value } });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching port suggestions:', error);
    }
  };

  const inputProps = (name, value, onChange) => {
    let placeholder = `Enter ${name}`;

    if (name === 'origin') {
      if (search.carriageType === '1') {
        placeholder = 'Enter CFS origin';
      } else if (search.carriageType === '0') {
        placeholder = 'Enter Port of Discharge';
      }
    } else if (name === 'destination') {
      if (search.carriageType === '1') {
        placeholder = 'Enter Port of Load';
      } else if (search.carriageType === '0') {
        placeholder = 'Enter CFS Destination';
      }
    }

    return {
      placeholder,
      value,
      onChange
    };
  };

  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handleJumpToPage = (pageNumber) => setCurrentPage(pageNumber);

  const getOriginLabel = () => {
    if (search.carriageType === '1') {
      return 'CFS Origin(s)';
    } else if (search.carriageType === '0') {
      return 'Port(s) of Discharge';
    }
    return 'Origin';
  };

  const getDestinationLabel = () => {
    if (search.carriageType === '1') {
      return 'Port(s) of Loading';
    } else if (search.carriageType === '0') {
      return 'CFS Destination(s)';
    }
    return 'Destination';
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <h2>Carriage Rules</h2>
        <Dropdown>
          <Dropdown.Toggle variant="primary" id="dropdown-basic">
            Options
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item as={Link} to="/rules/carriage/add">
              <FaPlus className="me-1" /> <FaTruck className="me-2" /> Create Carriage Rule
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}
      <form className="search-fields" onSubmit={handleSearchSubmit}>
        <div className="form-group">
          <label htmlFor="carriageType" >Carriage Type</label>
          <div>
            <select
              className="form-select"
              name="carriageType"
              id="carriageType"
              value={search.carriageType}
              onChange={handleSearchChange}
            >
              <option value="2">Any</option>
              <option value="1">Pre-Carriage</option>
              <option value="0">On-Carriage</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <Autosuggest
            suggestions={originSuggestions}
            onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setOriginSuggestions)}
            onSuggestionsClearRequested={() => setOriginSuggestions([])}
            getSuggestionValue={(suggestion) => suggestion.port_name}
            renderSuggestion={(suggestion) => <div>{suggestion.port_name}</div>}
            inputProps={{
              ...inputProps('origin', search.origin, (e, { newValue }) => handleSearchChange(e, { newValue })),
              name: 'origin'
            }}
            theme={{
              container: 'react-autosuggest__container',
              input: 'react-autosuggest__input',
              suggestionsContainer: 'react-autosuggest__suggestions-container',
              suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
              suggestionsList: 'react-autosuggest__suggestions-list',
              suggestion: 'react-autosuggest__suggestion',
              suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
            }}
          />
        </div>
        <div className="form-group">
          <Autosuggest
            suggestions={destinationSuggestions}
            onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setDestinationSuggestions)}
            onSuggestionsClearRequested={() => setDestinationSuggestions([])}
            getSuggestionValue={(suggestion) => suggestion.port_name}
            renderSuggestion={(suggestion) => <div>{suggestion.port_name}</div>}
            inputProps={{
              ...inputProps('destination', search.destination, (e, { newValue }) => handleSearchChange(e, { newValue })),
              name: 'destination'
            }}
            theme={{
              container: 'react-autosuggest__container',
              input: 'react-autosuggest__input',
              suggestionsContainer: 'react-autosuggest__suggestions-container',
              suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
              suggestionsList: 'react-autosuggest__suggestions-list',
              suggestion: 'react-autosuggest__suggestion',
              suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
            }}
          />
        </div>
        {/* <input
          type="text"
          name="appliesTo"
          placeholder="Applies To"
          value={search.appliesTo}
          onChange={handleSearchChange}
        /> */}
        <Button type="submit" variant="primary" className="btn btn-primary search-button"><BiSearch /></Button>
      </form>
      {rules.length === 0 ? (
        <p>No carriage rules found</p>
      ) : (
        <table className="table table-striped">
          <thead>
            <tr>
              <th onClick={() => requestSort('pre_carriage_flg')}>Carriage Type {getSortIcon('pre_carriage_flg')}</th>
              <th onClick={() => requestSort('origin_port_code_list')}>{getOriginLabel()} {getSortIcon('origin_port_code_list')}</th>
              <th onClick={() => requestSort('destination_port_code_list')}>{getDestinationLabel()} {getSortIcon('destination_port_code_list')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRules.map((rule) => (
              <tr key={rule.carriage_rule_id}>
                <td>{rule.pre_carriage_flg === 1 ? `Pre-Carriage` : `On-Carriage`}</td>
                <td>{rule.origin_port_code_list}</td>
                <td>{rule.destination_port_code_list}</td>
                <td>
                  <OverlayTrigger
                    overlay={<Tooltip id={`tooltip-edit-${rule.carriage_rule_id}`}>Edit the carriage rule</Tooltip>}
                  >
                    <Button variant="warning" className="action-button" as={Link} to={`/rules/carriage/edit/${rule.carriage_rule_id}`}>
                      <BiEdit />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    overlay={<Tooltip id={`tooltip-delete-${rule.carriage_rule_id}`}>Delete the carriage rule</Tooltip>}
                  >
                    <Button variant="danger" className="action-button" onClick={() => handleDelete(rule)}>
                      <BiTrash />
                    </Button>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {totalPages > 1 && (
        <Pagination className="justify-content-center mt-3">
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={handleFirstPage} disabled={currentPage === 1}>
              <BiFirstPage /> <Tooltip content="First" place="top" />
            </Button>
            <Button variant="outline-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
              <BiChevronLeft /> <Tooltip content="Previous" place="top" />
            </Button>
            <DropdownButton
              as={ButtonGroup}
              variant="outline-secondary"
              title={`Jump to`}
              id="bg-nested-dropdown"
              className="jump-to-dropdown"
            >
              <div className="scrollable-dropdown">
                {[...Array(totalPages).keys()].map((page) => (
                  <Dropdown.Item key={page + 1} onClick={() => handleJumpToPage(page + 1)}>
                    Page {page + 1}
                  </Dropdown.Item>
                ))}
              </div>
            </DropdownButton>
            <Button variant="outline-secondary" onClick={handleNextPage} disabled={currentPage === totalPages}>
              <BiChevronRight /> <Tooltip content="Next" place="top" />
            </Button>
            <Button variant="outline-secondary" onClick={handleLastPage} disabled={currentPage === totalPages}>
              <BiLastPage /> <Tooltip content="Last" place="top" />
            </Button>
            <div className="pagination-info">
              <span>Page {currentPage} of {totalPages}</span>
            </div>
          </ButtonGroup>
        </Pagination>
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Carriage Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Deleting this record is permanent and cannot be undone.</p>
          {selectedRule && (
            <table className="table">
              <thead>
                <tr>
                  <th>Origin</th>
                  <th>Destination</th>
                  <th>Applies To</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedRule.origin_port_code_list}</td>
                  <td>{selectedRule.destination_port_code_list}</td>
                  <td>{selectedRule.applies_to_origin_destination}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete Carriage Rule</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CarriageRules;
